/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { decompress as zstdDecompressPureJS } from 'fzstd';
import { EitherAsync, Left, Right } from 'purify-ts';
import * as codec from 'purify-ts/Codec';
// @ts-expect-error: This library doesn't have type annotations yet.
import * as snappyJs from 'snappyjs';
import { assert, errors, expr, object, parsers } from '@codegrade/utils';
import * as zstd from '@codegrade/zstd';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class BaseCompressionError extends Error {
    /**
     * @param message The message of this error.
     * @param opts The opts for the error.
     */
    constructor(message, opts) {
        super(message, opts);
        this.name = this.constructor.name;
    }
}
/** This error is returned when decompression failed. */
export class DecompressionError extends BaseCompressionError {
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class CompressionError extends BaseCompressionError {
}
/**
 * This error is returned when a while reading a compressed binary the magic number (which is the
 * first byte) is unknown.
 */
export class InvalidCompressionMagicNumber extends BaseCompressionError {
    /**
     * @param invalid The (invalid) magic number that was found.
     * @param opts The opts for the error.
     */
    constructor(invalid, opts) {
        super(`Invalid compression magic number: ${invalid}`, opts);
    }
}
/**
 * All compressions supported by CodeGrade.
 *
 * When adding a new compression here you also should update `safe/utils/compression.go`.
 */
export const ALL_COMPRESSIONS = ['snappy', 'zstd', 'none'];
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const compressionParser = parsers.exactly(...ALL_COMPRESSIONS);
const COMPRESSION_MAGIC_LOOKUP = {
    none: 0,
    snappy: 1,
    zstd: 2,
};
const COMPRESSION_MAGIC_REVERSE_LOOKUP = /* #__PURE__ */ object.fromEntries(ALL_COMPRESSIONS.map(comp => [COMPRESSION_MAGIC_LOOKUP[comp], comp]));
/**
 * Do we guess that a given data array is normal text.
 *
 * @param data The data to guess for if it is text.
 * @returns A guess if we think this is normal text. This is currently done by looking at the first
 * byte and seeing if it is a normal character. Where normal is defined as something that often
 * occurs in text.
 */
function looksLikeText(data) {
    const start = data[0];
    return (start != null &&
        // This is a number
        ((start >= 48 && start <= 57) ||
            // This is A-Z
            (start >= 65 && start <= 90) ||
            // This is a-z
            (start >= 97 && start <= 122) ||
            // This is a space
            start === 32 ||
            // This is a period
            start === 46 ||
            // This is a comma
            start === 43 ||
            // This is a /, which we include to also allow code to return true.
            start === 47 ||
            // This is a #, which we include to also allow code to return true.
            start === 35));
}
const MIN_COMPRESSION_LENGTH = 100;
/** File are considered large if they are 1MB. */
const LARGE_FILE_SIZE = (1 << 10) << 10;
/**
 * Get the default compression we should use for a given piece of data.
 *
 * @param data The data for which to get the default compression.
 * @returns The compression to use, for small buffers this will be `none` (the latency of
 * compression is not worth the potential minor space savings), for stuff that looks like text and
 * is not too long (if it is very long the extra time of ZSTD is probably worth it) we use `snappy`
 * and for all other we use `zstd`.
 */
function getDefaultCompression(data) {
    if (data.length <= MIN_COMPRESSION_LENGTH) {
        return 'none';
    }
    else if (data.byteLength < LARGE_FILE_SIZE && looksLikeText(data)) {
        return 'snappy';
    }
    else {
        return 'zstd';
    }
}
/** The result of compression. */
export class CompressionResult {
    compression;
    data;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(compression, data) {
        this.compression = compression;
        this.data = data;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromOldStyle({ data, compression }) {
        return new CompressionResult(compression, data);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get compressedData() {
        return this.data;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get compressedByteLength() {
        return this.data.byteLength;
    }
    /** @returns A Promise that resolves to the decompressed data. */
    async decompressPromise() {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return await decompressPromise(this);
    }
    /** @returns An EitherAsync with the decompressed data. */
    decompress() {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return decompress(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    toCompressedBinary() {
        const res = new ArrayBuffer(this.data.byteLength + 1);
        const view = new Uint8Array(res);
        const magic = COMPRESSION_MAGIC_LOOKUP[this.compression];
        view[0] = magic;
        view.set(this.data, 1);
        return view;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromCompressedBinary(data) {
        const magic = data[0];
        const compression = COMPRESSION_MAGIC_REVERSE_LOOKUP[magic];
        if (compression == null) {
            return Left(new InvalidCompressionMagicNumber(magic));
        }
        const buffer = data.buffer;
        return Right(new CompressionResult(compression, new Uint8Array(buffer, data.byteOffset + 1, data.byteLength - 1)));
    }
    /**
     * Decompresses the current data and potentially recompresses it for optimization.
     *
     * It first decompresses the data. If the current compression matches the default for the
     * decompressed data and offers no size benefit, it might return 'none' compression or the
     * original. Otherwise, it attempts recompression and returns the shorter result (original or
     * new).
     *
     * @returns A Promise resolving to an object with:
     * - `compressed`: The optimal {@link CompressionResult} (possibly `this` or a new instance).
     * - `decompressed`: The `Uint8Array` of the decompressed data.
     */
    async maybeRecompress() {
        const decompressed = await this.decompressPromise();
        // An optimization: only recompress if the compression is different than the
        // compression we should use.
        if (this.compression === getDefaultCompression(decompressed)) {
            // Actually compression doesn't decrease length, so don't compress from now on.
            if (decompressed.byteLength <= this.compressedByteLength) {
                return {
                    compressed: new CompressionResult('none', decompressed),
                    decompressed,
                };
            }
            return { compressed: this, decompressed };
        }
        const compressed = await CompressionResult.compressPromise(decompressed);
        const shortestCompressed = compressed.compressedByteLength > this.compressedByteLength ? this : compressed;
        return {
            compressed: shortestCompressed,
            decompressed,
        };
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static async innerCompressWith(compression, data, opts) {
        try {
            switch (compression) {
                case 'zstd': {
                    const fromZstd = zstd.compress(data, opts?.zstdCompressionLevel).extract();
                    if (fromZstd != null) {
                        return new CompressionResult('zstd', await fromZstd);
                    }
                }
                // fallthrough
                case 'snappy':
                    return new CompressionResult('snappy', snappyJs.compress(data));
                default:
                    /* c8 ignore next */
                    return assert.AssertionError.assertNever(compression);
            }
        }
        catch (caught) {
            /* c8 ignore next */
            const err = errors.coerceToError(caught);
            /* c8 ignore next */
            throw new CompressionError(`Failed to compress: ${err.message}`, { cause: err });
        }
    }
    /**
     * Compress a piece of data.
     *
     * If no options are given the compression to use is determined by the
     * {@link getDefaultCompression} function. We ensure that the binary representation of the
     * compression result is at most one byte larger the input data if no compression is forced.
     *
     * @param data The data to compress.
     * @param opts The options to use, see {@link CompressionOptions} for what they mean.
     * @returns The compressed data.
     */
    static async compressPromise(data, opts) {
        let isForced;
        let compression;
        if (opts != null && opts.forcedCompression != null) {
            isForced = true;
            compression = opts.forcedCompression;
        }
        else {
            isForced = false;
            compression = getDefaultCompression(data);
        }
        if (compression === 'none' || data.length <= MIN_COMPRESSION_LENGTH) {
            return new CompressionResult('none', data);
        }
        const res = await CompressionResult.innerCompressWith(compression, data, opts);
        // If compression doesn't achieve anything don't use it as it will just waste cycles.
        if (!isForced && res.data.byteLength >= data.byteLength) {
            return new CompressionResult('none', data);
        }
        return res;
    }
    /**
     * Compress a piece of data.
     *
     * If no options are given the compression to use is determined by the
     * {@link getDefaultCompression} function. We ensure that the binary representation of the
     * compression result is at most one byte larger the input data if no compression is forced.
     *
     * @param data The data to compress.
     * @param opts The options to use, see {@link CompressionOptions} for what they mean.
     * @returns The compressed data.
     */
    static compress(data, opts) {
        return EitherAsync(async () => await CompressionResult.compressPromise(data, opts));
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const compressionResultParser = /* #__PURE__ */ (() => {
    const sub = codec.Codec.interface({
        compression: compressionParser,
        data: parsers.base64Parser,
    });
    return codec.Codec.custom({
        decode: val => sub
            .decode(val)
            .map(({ compression, data }) => new CompressionResult(compression, data)),
        encode: value => sub.encode({
            compression: value.compression,
            data: value.compressedData,
        }),
    });
})();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const getBinaryCompressionResultParser = (binaryCodec) => codec.Codec.custom({
    decode: val => binaryCodec
        .decode(val)
        .chain(binary => CompressionResult.fromCompressedBinary(binary).mapLeft(err => err.message)),
    encode: value => binaryCodec.encode(value.toCompressedBinary()),
});
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const base64CompressionResultParser = /* #__PURE__ */ getBinaryCompressionResultParser(parsers.base64Parser);
/** @inheritdoc */
export const compress = CompressionResult.compress;
/** @inheritdoc */
export const compressPromise = CompressionResult.compressPromise;
/**
 * Decompress compressed data.
 *
 * @param val The value to decompress.
 * @returns A promise that resolves to the decompressed value.
 */
export async function decompressPromise(val) {
    const data = val instanceof CompressionResult ? val.compressedData : val.data;
    try {
        return await expr.match(val, 'compression', {
            snappy: () => snappyJs.uncompress(data),
            zstd: () => zstd.decompress(data),
            none: async () => data,
        });
    }
    catch (caught) {
        const err = errors.coerceToError(caught);
        throw new DecompressionError(`Failed to decompress: ${err.message}`, { cause: err });
    }
}
/**
 * Decompress compressed data.
 *
 * @param val The value to decompress.
 * @returns An {@link EitherAsync} that resolves to the decompressed value.
 */
export function decompress(val) {
    return EitherAsync(async () => await decompressPromise(val));
}
/**
 * Same as `decompress` but is synchronous.
 *
 * This function may only be used in testing code. Right now it blocks as much as `decompress`,
 * however in the future this might not be the case.
 *
 * @param val The value to decompress.
 * @returns The decompressed value, it will raise if decompressing failed.
 */
export function decompressSyncTestingOnly(val) {
    const data = val instanceof CompressionResult ? val.compressedData : val.data;
    return expr.match(val, 'compression', {
        none: () => data,
        zstd: () => zstdDecompressPureJS(data),
        snappy: () => snappyJs.uncompress(data),
    });
}
